import React from 'react';
import { Button } from 'antd';
import cx from 'classnames';

import { downloadFile } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import styles from './TermsAndConditions.module.less';

const downloadUrl = `${process.env.REACT_APP_STORAGE_API_BASE_URL}/public/terms-and-conditions/backoffice/terms-and-conditions-v0.pdf`;
const fileName = 'TermsAndConditions.pdf';

export default function TermsAndConditions() {
  const handleDownloadClick = async () => {
    try {
      await downloadFile({ downloadUrl, fileName });
    } catch (e: any) {
      BaseAlert.error(e.message);
    }
  };

  return (
    <div className={cx(styles.termsAndConditions, 'page-wrapper scroll-box')}>
      <div className="page-box page-holder">
        <div className={cx(styles.inner, 'page-inner')}>
          <div className={styles.topBox}>
            <h1>Terms and Conditions</h1>

            <Button
              className="with-icon"
              type="primary"
              size="large"
              onClick={handleDownloadClick}
            >
              <i className="icon icon-pdf" />
              <span>Download</span>
            </Button>
          </div>

          <div className={styles.content}>
            <h2>
              Acceptance of the Terms of Use
            </h2>
            <p>
              These terms of use are entered into by and between you (“you”, “your” or “user”) and MYBASEPAY
              LLC ("<strong>Company</strong>," "<strong>we</strong>," or "<strong>us</strong>"), together with any documents they expressly incorporate by reference
              (collectively, "Terms of Use"), and they govern your access to and use of mybasepay.com, whether as a
              guest, registered user or Client (as defined below), including any content, functionality, and services
              offered on or through mybasepay.com (the "<strong>Website</strong>"), including, but not limited to our vendor
              management system (our <strong>“VMS”</strong>).
            </p>

            <p>
              Please read these Terms of Use carefully before you start to use the Website. By using the Website or by
              clicking to accept or agree to these Terms of Use when this option is made available to you, you accept
              and agree to be bound and abide by these Terms of Use and our <i>Privacy Policy</i>. If you do not agree to these Terms of Use or the
              <i> Privacy Policy</i>, you must not access or use the Website. 
            </p>

            <p>
              This Website is offered and is available to users who are 18 years of age or older and reside in the United
              States or any of its territories or possessions. By using this Website, you represent and warrant that you
              are of legal age to form a binding contract with the Company and meet all of the foregoing eligibility
              requirements. If you are using the Website on behalf of your employer or other entity, your use creates a
              legally binding contract between your employer or entity and us. If you do not meet all of these
              requirements, you must not access or use the Website.
            </p>

            <h2>
              Changes to the Terms of Use
            </h2>

            <p>
              We may revise and update these Terms of Use from time to time in our sole discretion. All changes are
              effective immediately when we post them, and apply to all access to and use of the Website thereafter.
              However, any changes to the dispute resolution provisions set out in <a href="#governing-anchor"> Governing Law and Jurisdiction </a> 
              section of these Terms of Use will not apply to any disputes for which the parties have actual notice on or
              before the date the change is posted on the Website. 
            </p>

            <p>
              Your continued use of the Website following the posting of revised Terms of Use means that you accept
              and agree to the changes. You are expected to check this page each time you access this Website so you
              are aware of any changes, as they are binding on you. 
            </p>

            <h2>
              Services Offered
            </h2>

            <p>
              We offer a variety of back office solutions, including, but not limited to, vendor of record, employer of
              record, payroll, performance tracking and payroll financing services (collectively <strong>“Services”</strong>), to staffing
              companies and end-user business clients (<strong>“Clients”</strong>), to each according to a Master Services Agreement
              (<strong>“Master Services Agreement”</strong> or <strong>“MSA”</strong>) that is separate from these Terms of Use. All Services are
              provided to Clients according to and subject to the Master Services Agreement, and no Services are
              rendered to you or any party, absent entry into the Master Services Agreement.
            </p>

            <p>
              This Website is made available to provide all Users information on the Services, obtain relevant
              information from registered users, allow for communications with registered users, provide Clients access
              to our VMS, and facilitate the rendering of the Services to Clients according to their Master Services
              Agreements. 
            </p>

            <h2>
              Accessing the Website and Account Security
            </h2>

            <p>
              We reserve the right to disable or modify this Website and any service or content we provide on the
              Website in our sole discretion without notice. We will not be liable if for any reason all or any part of the
              Website is unavailable at any time or for any period. From time to time, we may restrict access to some
              parts of the Website, or the entire Website, to users, including registered users or Clients.
              You are responsible for all arrangements necessary for you to have access to the Website, and to ensure
              that all persons who access the Website through your internet connection are aware of these Terms of Use
              and comply with them.
            </p>

            <p>
              To access the Website or some of the resources it offers, you may be asked to provide certain registration
              details or other information. It is a condition of your use of the Website that all the information you
              provide on the Website is correct, current, and complete..
            </p>

            <p>
              If you choose, or are provided with, a user name, password, or any other piece of information as part of
              our security procedures, you must treat such information as confidential, and you must not disclose it to
              any other person or entity. You also acknowledge that your account is personal to you and your company
              or entity, if applicable, and agree not to provide any other person with access to this Website or portions
              of it using your user name, password, or other security information. You agree to notify us immediately
              of any unauthorized access to or use of your user name or password or any other breach of security. You
              also agree to ensure that you exit from your account at the end of each session. You should use particular
              caution when accessing your account from a public or shared computer so that others are not able to view
              or record your password or other personal information.
            </p>

            <p>
              You agree that all information you provide to register with this Website or otherwise, including, but not
              limited to, through the use of any interactive features on the Website, and all information we collect on
              this Website is subject to and governed by our <i> Privacy Policy</i>, and by
              using this Website, you consent to all actions we take with respect to your information consistent with our
              <i> Privacy Policy</i>.
            </p>

            <p>
              We have the right to disable any user name, password, or other identifier, whether chosen by you or
              provided by us, at any time in our sole discretion for any or no reason, including if, in our opinion, you
              have violated any provision of these Terms of Use.
            </p>

            <h2>
              Intellectual Property Rights and Prohibited Uses of Site
            </h2>

            <h3>
              License
            </h3>

            <p>
              These Terms of Use grant you a personal, nontransferable, nonexclusive, revocable, limited license to
              access, view, download and use the Website, our VMS, and all content we provide to you through the Site
              (<strong>“MBP Content”</strong>) solely for your internal business purposes that relate to our Services and the provision
              of Services to Clients according to their Master Services Agreements. If you breach any of these Terms of
              Use, your rights and this license will terminate, you must immediately cease using the MBP Content, and
              you must return or destroy (with written certification to us) all copies, including electronic copies, of
              MBP Content then in your possession or control. Without limiting the foregoing, we may terminate this
              license at any time and for any reason at our sole and absolute discretion.
            </p>

            <h3>
              Ownership by Company
            </h3>

            <p>
              The Website and its entire contents, features, and functionality (including but not limited to all
              information, software, text, displays, images, video, and audio, and the design, selection, and arrangement
              thereof) are owned by the Company, its licensors, or other providers of such content and are protected by
              United States and international copyright, trademark, patent, trade secret, and other intellectual property
              or proprietary rights laws. We own and will continue to own all rights, title and interest in and to
              myBasePay, mybasepay.com, our VMS, MBP Content, our logo, trademarks and service marks, product
              and service names, designs, and slogans of the Company or its affiliates and all intellectual property rights
              in connection with the foregoing (e.g., without limitation, copyright, trademark, patent and trade secret
              rights). You must not use such marks without the prior written permission of the Company. All other
              names, logos, product and service names, designs, and slogans on this Website of our licensors are the
              trademarks of their respective owners. No ownership interests will be transferred to you simply by your
              use of the Site.
            </p>

            <h3>
              Prohibited Uses of Intellectual Property
            </h3>

            <p>
              You must not copy, reproduce, modify, create derivative works of or improvements, publicly display,
              publicly perform, republish, download, store, transmit, transfer, distribute, license, lease or assign any of
              the content on our Website or any features or functionality of our Site, or otherwise delete or alter any
              copyright, trademark or other proprietary rights notices from content from the Site, except as follows:
            </p>

            <ul>
              <li>
                Your computer may temporarily store copies of such content in RAM incidental
                to your accessing and viewing such content.
              </li>
              <li>
                You may store files that are automatically cached by your Web browser for
                display enhancement purposes.
              </li>
              <li>
                You may print or download one copy of a reasonable number of pages of the
                Website for your own personal use and not for further reproduction, publication,
                or distribution.
              </li>
              <li>
                If we provide desktop, mobile, or other applications for download, you may
                download a single copy to your computer or mobile device solely for your own
                personal use, provided you agree to be bound by our end user license agreement
                for such applications.
              </li>
            </ul>

            <p>
              If you violate any of the foregoing, including, but not limited to, if you print, copy, modify, download, or
              otherwise use or provide any other person with access to any part of the Website in breach of the Terms
              of Use, your right to use the Website will cease immediately and you must, at our option, return or
              destroy any copies of the materials you have made. No right, title, or interest in or to the Website or any
              content on the Website is transferred to you, and all rights not expressly granted are reserved by the
              Company. Any use of the Website not expressly permitted by these Terms of Use is a breach of these
              Terms of Use and may violate copyright, trademark, and other laws.
            </p>

            <h3>
              Prohibited Activities
            </h3>

            <p>
              You may use the Website only for lawful purposes and in accordance with these Terms of Use. You agree
              not to use the Website:
            </p>

            <ul>
              <li>
                In any way that violates any applicable federal, state, local, or international law
                or regulation (including, without limitation, any laws regarding the export of data
                or software to and from the US or other countries).
              </li>
              <li>
                For the purpose of exploiting, harming, or attempting to exploit or harm minors
                in any way by exposing them to inappropriate content, asking for personally
                identifiable information, or otherwise.
              </li>
              <li>
                To send, knowingly receive, upload, download, use, or re-use any material that
                does not comply these Terms of Use.
              </li>
              <li>
                To transmit, or procure the sending of, any advertising or promotional material,
                including any "junk mail," "chain letter," "spam," or any other similar
                solicitation.
              </li>
              <li>
                To impersonate or attempt to impersonate the Company, a Company employee,
                another user, or any other person or entity (including, without limitation, by
                using email addresses or screen names associated with any of the foregoing).
              </li>
              <li>
                To engage in any other conduct that restricts or inhibits anyone's use of the
                Website, or which, as determined by us, may harm the Company or users of the
                Website, or expose them to liability.
              </li>
            </ul>
            
            <p>
              Additionally, you agree not to:
            </p>

            <ul>
              <li>
                Use the Website in any manner that could disable, overburden, damage, or impair
                the site or interfere with any other party's use of the Website, including their
                ability to engage in real time activities through the Website.
              </li>
              <li>
                Use any robot, spider, or other automatic device, process, or means to access the
                Website for any purpose, including monitoring or copying any of the material on
                the Website.
              </li>
              <li>
                Use any manual process to monitor or copy any of the material on the Website,
                or for any other purpose not expressly authorized in these Terms of Use, without
                our prior written consent.
              </li>
              <li>
                Use any device, software, or routine that interferes with the proper working of
                the Website.
              </li>
              <li>
                Introduce any viruses, Trojan horses, worms, logic bombs, or other material that
                is malicious or technologically harmful.
              </li>
              <li>
                Attempt to gain unauthorized access to, interfere with, damage, or disrupt any
                parts of the Website, the server on which the Website is stored, or any server,
                computer, or database connected to the Website.
              </li>
              <li>Attack the Website via a denial-of-service attack or a distributed denial-ofservice attack.</li>
              <li>Otherwise attempt to interfere with the proper working of the Website.</li>
            </ul>

            <h2>
              User Contributions
            </h2>

            <h3>
              Rights to User Contributions
            </h3>

            <p>
              As a registered user or Client, you will submit or upload certain information pertaining to your business
              and personally identifiable information. All information you submit or upload on or through the Website
              shall be referred to as <strong>“User Contributions"</strong>.
            </p>

            <p>
              All User Contributions must comply with the <span>Content Standards</span> set out in these Terms of Use. You grant
              to us a non-exclusive, worldwide, royalty-free and fully paid license to use, reformat, display, modify and
              create derivative works of the User Contributions to the extent necessary for us to provide you with
              information you request on our Services and to Clients with Services according to our Master Services
              Agreement. You agree the foregoing license includes, as necessary, our right to share your User 
            </p>

            <p>
              Contributions with third parties consistent with our Master Services Agreement. To the extent User
              Contributions consist of personally identifiable information, the terms of our <i>Privacy Policy</i> will apply.
            </p>

            <p>
              You represent and warrant that you own or control all rights in and to the User Contributions and have the
              right to grant the license granted above to us and our affiliates and service providers, and each of their and
              our respective licensees, successors, and assigns. Moreover, you represent that and warrant all of your
              User Contributions do and will comply with these Terms of Use.
            </p>

            <p>
              You understand and acknowledge that you are responsible for any User Contributions you submit or
              contribute, and you, not the Company, have full responsibility for such content, including its legality,
              reliability, accuracy, and appropriateness. We are not responsible or liable to any third party for the
              content or accuracy of any User Contributions supplied by you or any other user of the Website.
            </p>

            <p>
              Any User Contributions that consist of recommendations, suggestions, feedback and similar
              communications shall be considered nonproprietary, non-confidential and, unless you include personally
              identifiable information as described in our Privacy Policy, non-personal information. In regard to such
              User Contributions, you hereby grant us a perpetual, assignable, unrestricted, worldwide, royalty-free,
              irrevocable right to use, reproduce, display, perform, practice, modify, create derivative or collective
              works, transmit and distribute same, in whole or in part, and including all intellectual property rights
              therein.
            </p>

            <h3>
              Contribution Standards
            </h3>

            <p>
              These contribution standards apply to any and all User Contributions. User Contributions must in their
              entirety comply with all applicable federal, state, local, and international laws and regulations. Without
              limiting the foregoing, User Contributions must not:
            </p>

            <ul>
              <li>
                Contain any material that is defamatory, obscene, indecent, abusive, offensive,
                harassing, violent, hateful, inflammatory, or otherwise objectionable.
              </li>
              <li>
                Promote sexually explicit or pornographic material, violence, or discrimination
                based on race, sex, religion, nationality, disability, sexual orientation, or age.
              </li>
              <li>
                Infringe any patent, trademark, trade secret, copyright, or other intellectual
                property or other rights of any other person.
              </li>
              <li>
                Violate the legal rights (including the rights of publicity and privacy) of others or
                contain any material that could give rise to any civil or criminal liability under
                applicable laws or regulations or that otherwise may be in conflict with these
                Terms of Use and our <i> Privacy Policy</i>.
              </li>
              <li>Be likely to deceive any person.</li>
              <li>
                Promote any illegal activity, or advocate, promote, or assist any unlawful act.
                Cause annoyance, inconvenience, or needless anxiety or be likely to upset,
                embarrass, alarm, or annoy any other person.
              </li>
              <li>
                Impersonate any person, or misrepresent your identity or affiliation with any
                person or organization.
              </li>
              <li>
                Involve commercial activities or sales, such as contests, sweepstakes, and other
                sales promotions, barter, or advertising.
              </li>
              <li>
                Give the impression that they emanate from or are endorsed by us or any other
                person or entity, if this is not the case.
              </li>
            </ul>

            <h3>
              Monitoring and Enforcement; Termination
            </h3>

            <p>
              We have the right to:
            </p>

            <ul>
              <li>
                Remove or refuse to post any User Contributions for any or no reason in our sole
                discretion.
              </li>
              <li>
                Take any action with respect to any User Contribution that we deem necessary or
                appropriate in our sole discretion, including if we believe that such User
                Contribution violates the Terms of Use, including the Contribution Standards,
                infringes any intellectual property right or other right of any person or entity,
                threatens the personal safety of users of the Website or the public, or could create
                liability for the Company.
              </li>
              <li>
                Disclose your identity or other information about you to any third party who
                claims that material posted by you violates their rights, including their
                intellectual property rights or their right to privacy.
              </li>
              <li>
                Take appropriate legal action, including without limitation, referral to law
                enforcement, for any illegal or unauthorized use of the Website.
              </li>
              <li>
                Terminate or suspend your access to all or part of the Website for any or no
                reason, including without limitation, any violation of these Terms of Use.
              </li>
            </ul>

            <p>
              Without limiting the foregoing, we have the right to cooperate fully with any law enforcement authorities
              or court order requesting or directing us to disclose the identity or other information of any user of the
              Website. YOU WAIVE AND HOLD HARMLESS THE COMPANY AND ITS AFFILIATES,
              LICENSEES, AND SERVICE PROVIDERS FROM ANY CLAIMS RESULTING FROM ANY
              ACTION TAKEN BY ANY OF THE FOREGOING PARTIES DURING, OR TAKEN AS A
              CONSEQUENCE OF, INVESTIGATIONS BY EITHER SUCH PARTIES OR LAW ENFORCEMENT
              AUTHORITIES.
            </p>

            <p>
              However, we do not undertake to review all content before it is posted on the Website, and cannot ensure
              prompt removal of objectionable material after it has been posted. Accordingly, we assume no liability for
              any action or inaction regarding transmissions, communications, or content provided by any user or third
              party. We have no liability or responsibility to anyone for performance or nonperformance of the
              activities described in this section.
            </p>

            <h2>
              Links
            </h2>

            <h3>
              Links to Website
            </h3>

            <p>
              You may link to our homepage, provided you do so in a way that is fair and legal and does not damage
              our reputation or take advantage of it, but you must not establish a link in such a way as to suggest any
              form of association, approval, or endorsement on our part without our express written consent or pursuant
              to a Master Services Agreement.
            </p>

            <p>
              This Website may provide certain features that enable you to link from your own or certain third-party
              websites to certain content on this Website, send emails or other communications with certain content, or
              links to certain content, on this Website, or cause limited portions of content on this Website to be
              displayed or appear to be displayed on your own or certain third-party websites. You may use these
              features solely as they are provided by us, and otherwise in accordance with any additional terms and
              conditions we provide with respect to such features.
            </p>

            <p>
              Subject to the foregoing, you must not establish a link from any website that is not owned by you, cause
              the Website or portions of it to be displayed on, or appear to be displayed by, any other site (for example,
              framing, deep linking, or in-line linking), link to any part of the Website other than the homepage, or
              otherwise take any action with respect to the materials on this Website that is inconsistent with any other
              provision of these Terms of Use. You agree to cooperate with us in causing any unauthorized framing or
              linking to cease immediately. We reserve the right to withdraw linking permission without notice. We
              may disable all or any links at any time without notice in our discretion.
            </p>

            <h3>
              Links Included in User Contributions
            </h3>

            <p>
              User Contributions may include links that enable us to access websites that are owned and operated by
              third parties for purposes of accessing information that may be useful in the provision of Services. You
              represent that you have obtained all necessary permissions from any third parties to provide us with such
              links and access to such third party sites and content.
            </p>

            <h3>
              Links from the Website
            </h3>

            <p>
              If the Website contains links to other sites or to resources provided by third parties, including, but not
              limited to, links contained in advertisements, these links are provided for your convenience only. You
              acknowledge that Company is not affiliated in any way with any third party sites, and that if you click on
              a link to a third party site, once you have left our Website, we have no further control over the content and
              materials that you will be viewing on the third party site and accept no responsibility for them or for any
              loss or damage that may arise from your use of their sites. We are not responsible for the content,
              accuracy, or opinions expressed on any third party site. We will not, and are under no obligation to,
              investigate, monitor or check the third party sites for accuracy or completeness, or for any obscene,
              scandalous, inflammatory, pornographic, indecent, profane, defamatory or unlawful content or materials.
              Inclusion of any link to a third party site that is placed on our Website does not imply approval or
              endorsement of the third party site. If you decide to leave our Website and access a third party site, you do
              so at your own risk and any information you obtain or use is at your sole responsibility and liability. We
              are not responsible for the privacy policies applicable to third party sites. Once you link to a third party
              site, our <i>Privacy Policy</i> is no longer in effect.
            </p>

            <h2>
              Reliance on Information Posted
            </h2>

            <p>
              The information presented on or through the Website is made available solely for general informational
              purposes. Moreover, we may update the content on this Website from time to time, but its content is not
              necessarily complete or up-to-date. Any of the content on the Website may be out of date at any given
              time, and we are under no obligation to update such material. We do not warrant the accuracy,
              completeness, or usefulness of any information presented on or through our Website. Any reliance you
              place on such information is strictly at your own risk. We disclaim all liability and responsibility arising
              from any reliance placed on such content by you or any other visitor to the Website, or by anyone who
              may be informed of any of its contents.
            </p>
            <p>
              This Website may include User Contributions provided by third parties, including materials provided by
              other users, bloggers, and third-party licensors, syndicators, aggregators, and/or reporting services. All
              statements and/or opinions expressed in such content, and all articles and responses to questions and other
              content, other than the content provided by the Company, are solely the opinions and the responsibility of
              the person or entity providing those materials. Such content does not necessarily reflect the opinion of the
              Company. We are not responsible, or liable to you or any third party, for the content or accuracy of any
              materials provided by any third parties.
            </p>

            <h2>Geographic Restrictions</h2>

            <p>
              The owner of the Website is based in the State of Florida in the United States. We provide this Website
              for use only by persons located in the United States. We make no claims that the Website or any of its
              content is accessible or appropriate outside of the United States. Access to the Website may not be legal
              by certain persons or in certain countries. You are not authorized to access the Website from outside the
              United States and if you violate such restriction, you do so on your own initiative and you are responsible
              for compliance with all applicable laws, rules and regulations and you expressly indemnify and hold the
              Company harmless for any loss or damage incurred by Company as a result of your violation.
            </p>

            <h2>Disclaimer of Warranties</h2>

            <p>
              You understand that we cannot and do not guarantee or warrant that files available for downloading from
              the internet or the Website will be free of viruses or other destructive code. You are responsible for
              implementing sufficient procedures and checkpoints to satisfy your particular requirements for anti-virus
              protection and accuracy of data input and output, and for maintaining a means external to our Site for any
              reconstruction of any lost data.
            </p>
            <p>
              TO THE FULLEST EXTENT PROVIDED BY LAW, WE WILL NOT BE LIABLE FOR ANY LOSS
              OR DAMAGE CAUSED BY A DISTRIBUTED DENIAL-OF-SERVICE ATTACK, VIRUSES, OR
              OTHER TECHNOLOGICALLY HARMFUL MATERIAL THAT MAY INFECT YOUR COMPUTER
              EQUIPMENT, COMPUTER PROGRAMS, DATA, OR OTHER PROPRIETARY MATERIAL DUE
              TO YOUR USE OF THE WEBSITE OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE
              WEBSITE OR TO YOUR DOWNLOADING OF ANY MATERIAL POSTED ON IT, OR ON ANY
              WEBSITE LINKED TO IT.
            </p>
            <p>
              EXCEPT AS SET FORTH IN YOUR MASTER SERVICES AGREEMENT WITH THE COMPANY,
              YOUR USE OF THE WEBSITE, ITS CONTENT, AND ANY SERVICES OR ITEMS OBTAINED
              THROUGH THE WEBSITE IS AT YOUR OWN RISK. THE WEBSITE, ITS CONTENT, AND ANY
              SERVICES OR ITEMS OBTAINED THROUGH THE WEBSITE ARE PROVIDED ON AN "AS IS"
              AND "AS AVAILABLE" BASIS, WITHOUT ANY WARRANTIES OF ANY KIND, EITHER
              EXPRESS OR IMPLIED. NEITHER THE COMPANY NOR ANY PERSON ASSOCIATED WITH
              THE COMPANY MAKES ANY WARRANTY OR REPRESENTATION WITH RESPECT TO THE
              COMPLETENESS, SECURITY, RELIABILITY, QUALITY, ACCURACY, OR AVAILABILITY OF
              THE WEBSITE. WITHOUT LIMITING THE FOREGOING, NEITHER THE COMPANY NOR
              ANYONE ASSOCIATED WITH THE COMPANY REPRESENTS OR WARRANTS THAT THE
              WEBSITE, ITS CONTENT, OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE
              WEBSITE WILL BE ACCURATE, RELIABLE, ERROR-FREE, OR UNINTERRUPTED, THAT
              DEFECTS WILL BE CORRECTED, THAT OUR SITE OR THE SERVER THAT MAKES IT
              AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS, OR THAT THE
              WEBSITE OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE WEBSITE WILL
              OTHERWISE MEET YOUR NEEDS OR EXPECTATIONS.
            </p>
            <p>
              TO THE FULLEST EXTENT PROVIDED BY LAW, THE COMPANY HEREBY DISCLAIMS ALL
              WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, STATUTORY, OR
              OTHERWISE, INCLUDING BUT NOT LIMITED TO ANY WARRANTIES OF
              MERCHANTABILITY, NON-INFRINGEMENT, AND FITNESS FOR PARTICULAR PURPOSE.
            </p>
            <p>
              THE FOREGOING DOES NOT AFFECT ANY WARRANTIES THAT CANNOT BE EXCLUDED OR
              LIMITED UNDER APPLICABLE LAW, AND FURTHER, ANY WARRANTIES CONTAINED IN
              YOUR MASTER SERVICES AGREEMENT, AS IT RELATES TO THE SERVICES.
            </p>

            <h2>Limitation on Liability</h2>

            <p>
              TO THE FULLEST EXTENT PROVIDED BY LAW, IN NO EVENT WILL THE COMPANY, ITS
              AFFILIATES, OR THEIR LICENSORS, SERVICE PROVIDERS, EMPLOYEES, AGENTS,
              OFFICERS, OR DIRECTORS BE LIABLE FOR DAMAGES OF ANY KIND, UNDER ANY LEGAL
              THEORY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE, OR INABILITY TO USE,
              THE WEBSITE, ANY WEBSITES LINKED TO IT, ANY CONTENT ON THE WEBSITE OR SUCH
              OTHER WEBSITES, INCLUDING ANY DIRECT, INDIRECT, SPECIAL, INCIDENTAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO, PERSONAL
              INJURY, PAIN AND SUFFERING, EMOTIONAL DISTRESS, LOSS OF REVENUE, LOSS OF
              PROFITS, LOSS OF BUSINESS OR ANTICIPATED SAVINGS, LOSS OF USE, LOSS OF
              GOODWILL, LOSS OF DATA, AND WHETHER CAUSED BY TORT (INCLUDING
              NEGLIGENCE), BREACH OF CONTRACT, OR OTHERWISE, EVEN IF FORESEEABLE.
            </p>
            <p>
              The limitation of liability set out above does not apply to liability resulting from our gross negligence or
              willful misconduct or death or bodily injury caused by Services we render pursuant to a Master Services
              Agreement.
            </p>
            <p>
              THE FOREGOING DOES NOT AFFECT ANY LIABILITY THAT CANNOT BE EXCLUDED OR
              LIMITED UNDER APPLICABLE LAW.
            </p>

            <h2>Indemnification</h2>

            <p>
              You agree to defend, indemnify, and hold harmless the Company, its affiliates, licensors, and service
              providers, and its and their respective officers, directors, employees, contractors, agents, licensors,
              suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards,
              losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your
              violation of these Terms of Use or your use of the Website, including, but not limited to, your User
              Contributions, any use of the Website's content, services, and products other than as expressly authorized
              in these Terms of Use, or your use of any information obtained from the Website.
            </p>

            <h2 id="governing-anchor">Governing Law and Jurisdiction</h2>

            <p>
              All matters relating to the Website and these Terms of Use, and any dispute or claim arising therefrom or
              related thereto (in each case, including non-contractual disputes or claims), shall be governed by and
              construed in accordance with the internal laws of the State of Florida without giving effect to any choice
              or conflict of law provision or rule (whether of the State of Florida or any other jurisdiction).
            </p>
            <p>
              Any legal suit, action, or proceeding arising out of, or related to, these Terms of Use or the Website shall
              be instituted exclusively in the federal courts of the United States or the courts of the State of Florida, in
              each case located in Miami-Dade County. You waive any and all objections to the exercise of jurisdiction
              over you by such courts and to venue in such courts.
            </p>

            <h2>Limitation on Time to File Claims</h2>

            <p>
              ANY CAUSE OF ACTION OR CLAIM YOU MAY HAVE ARISING OUT OF OR RELATING TO
              THESE TERMS OF USE OR THE WEBSITE MUST BE COMMENCED WITHIN ONE (1) YEAR
              AFTER THE CAUSE OF ACTION ACCRUES; OTHERWISE, SUCH CAUSE OF ACTION OR
              CLAIM IS PERMANENTLY BARRED.
            </p>

            <h2>Waiver and Severability</h2>

            <p>
              No waiver by the Company of any term or condition set out in these Terms of Use shall be deemed a
              further or continuing waiver of such term or condition or a waiver of any other term or condition, and any
              failure of the Company to assert a right or provision under these Terms of Use shall not constitute a
              waiver of such right or provision.
            </p>
            <p>
              If any provision of these Terms of Use is held by a court or other tribunal of competent jurisdiction to be
              invalid, illegal, or unenforceable for any reason, such provision shall be eliminated or limited to the
              minimum extent such that the remaining provisions of the Terms of Use will continue in full force and
              effect.
            </p>

            <h2>Entire Agreement</h2>

            <p>
              The Terms of Use, our <i>Privacy Policy</i>, and the Master Services Agreement, if applicable, constitute the
              sole and entire agreement between you and myBasePay LLC regarding the Website and supersede all
              prior and contemporaneous understandings, agreements, representations, and warranties, both written and
              oral, regarding the Website.
            </p>

            <h2>
              Your Comments and Concerns
            </h2>

            <p>
              This website is operated by myBasePay LLC. All feedback, comments, requests for technical support, and
              other communications relating to the Website should be directed to: <a href="mailto:support@mybasepay.com.">support@mybasepay.com.</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
